import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";

interface FeedbackData {
  avgRating: number;
  totalResponses: number;
  distribution: { rating: number; count: number }[];
  topTags: { tag: string; count: number }[];
  recentComments: { rating: number; comment: string; timestamp: string }[];
}

export function FeedbackSummary({ data }: { data: FeedbackData }) {
  const maxCount = Math.max(...data.distribution.map((d) => d.count), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Visitor Feedback Summary</CardTitle>
        <CardDescription>
          Aggregates visitor feedback from in-app surveys. Average rating,
          distribution, and top tags highlight overall sentiment. Recent
          comments surface specific praise or complaints for the board.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.totalResponses === 0 ? (
          <p className="text-muted-foreground">No feedback in this period.</p>
        ) : (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{data.avgRating}</p>
                <p className="text-xs text-muted-foreground">Avg Rating</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{data.totalResponses.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Responses</p>
              </div>
            </div>

            {/* Rating distribution */}
            <div>
              <h4 className="text-sm font-medium mb-2">Distribution</h4>
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = data.distribution.find((d) => d.rating === rating)?.count ?? 0;
                  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={rating} className="flex items-center gap-2 text-sm">
                      <span className="w-4 text-right">{rating}</span>
                      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-8 text-right text-muted-foreground">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top tags */}
            {data.topTags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Top Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {data.topTags.map((t) => (
                    <Badge key={t.tag} variant="secondary">
                      {t.tag} ({t.count})
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Recent comments */}
            {data.recentComments.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Recent Comments</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Rating</TableHead>
                      <TableHead>Comment</TableHead>
                      <TableHead className="w-24">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recentComments.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>{"★".repeat(c.rating)}</TableCell>
                        <TableCell className="max-w-md truncate">{c.comment}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {new Date(c.timestamp).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
